import { useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import GrainOverlay from "@/components/GrainOverlay";
import ViewToggle from "@/components/ViewToggle";
import FavouritesWidget from "@/components/FavouritesWidget";
import HeartButton from "@/components/HeartButton";
import useGalleryData from "@/hooks/useGalleryData";
import useScrollTop from "@/hooks/useScrollTop";
import { resolveImage } from "@/lib/adminApi";

// Catalogue view — a calm 3-column square-tile grid. Tiles reveal a compact
// title + price on hover; clicking a tile opens the same immersive detail
// page used by the Hall. Deep-linkable at /paintings.
export default function GridGallery() {
  const navigate = useNavigate();
  const { gallery, paintings, loaded } = useGalleryData();

  useScrollTop();

  useEffect(() => {
    window.__ARTIST_EMAIL__ = gallery.artist_email;
  }, [gallery.artist_email]);

  const sorted = useMemo(
    () => [...paintings].sort((a, b) => (a.order || 0) - (b.order || 0)),
    [paintings]
  );

  const openPainting = (slug) => {
    navigate(`/paintings/${slug}`, { state: { from: "grid" } });
  };

  return (
    <div className="grid-root" data-testid="grid-root">
      <GrainOverlay />

      <nav className="grid-topbar" aria-label="Gallery navigation">
        <Link to="/" className="grid-brand" data-testid="grid-brand">
          <span className="grid-brand__name">{gallery.artist_name}</span>
          {gallery.hero_tagline && (
            <span className="grid-brand__tagline mono-caps" data-testid="grid-tagline">{gallery.hero_tagline}</span>
          )}
        </Link>
        <div className="grid-topbar__links">
          <Link to="/blog" className="grid-topbar__link mono-caps" data-testid="grid-nav-blog">Blog</Link>
          <a
            className="grid-topbar__contact mono-caps"
            href={`mailto:${gallery.artist_email}?subject=${encodeURIComponent("Inquiry — " + gallery.exhibition)}`}
            data-testid="grid-contact"
          >
            Contact
          </a>
        </div>
      </nav>

      <ViewToggle current="grid" />
      <FavouritesWidget />

      <header className="grid-header">
        <p className="grid-overline mono-caps">{gallery.hero_overline || "Selected works"}</p>
        <h1 className="grid-title">The Catalogue</h1>
        <p className="grid-sub">
          Every work in the exhibition, laid out at a glance. Click any piece to
          step into it.
        </p>
      </header>

      <main className="grid-wall" data-testid="grid-wall" data-loaded={loaded}>
        {loaded && sorted.length === 0 && (
          <p className="grid-empty" data-testid="grid-empty">New works are being hung — please return shortly.</p>
        )}
        {sorted.map((p) => {
          const src =
            p.image_url && p.image_url.includes("images.unsplash.com")
              ? `${p.image_url}&w=900&h=900&fit=crop`
              : resolveImage(p.image_url);
          return (
            <article key={p.id || p.slug} className="grid-tile" data-testid={`grid-tile-${p.order}`}>
              <HeartButton slug={p.slug} title={p.title} className="heart-btn--tile" testId={`heart-grid-${p.order}`} />
              <button
                type="button"
                className="grid-tile__hit"
                onClick={() => openPainting(p.slug)}
                data-cursor="Open"
                data-testid={`grid-open-${p.order}`}
                aria-label={`Open ${p.title}`}
              >
                <span className="grid-tile__frame">
                  <img
                    src={src}
                    alt={p.alt || p.title}
                    loading="lazy"
                    decoding="async"
                    draggable="false"
                  />
                  {p.status_tag && (
                    <span className="grid-tile__sticker" data-testid={`grid-sticker-${p.order}`}>
                      {p.status_tag}
                    </span>
                  )}
                  <span className="grid-tile__veil" aria-hidden="true" />
                </span>
                <span className="grid-tile__caption">
                  <span className="grid-tile__title" data-testid={`grid-title-${p.order}`}>
                    {p.title}
                  </span>
                  <span className="grid-tile__price mono-caps" data-testid={`grid-price-${p.order}`}>
                    {p.price_display}
                  </span>
                </span>
              </button>
            </article>
          );
        })}
      </main>

      <footer className="grid-foot">
        <Link to="/" className="grid-back mono-caps" data-testid="grid-back-to-hall">
          &larr; Return to the hall
        </Link>
        <span className="mono-caps grid-foot__meta">
          {sorted.length} works &middot; {gallery.artist_name}
        </span>
      </footer>
    </div>
  );
}
